import { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { jsPDF } from 'jspdf';
import wyenfosLogo from '../../../../assets/images/wyenfos.png';
import { getCompanyDetails, getLogoUrl } from '../utils/companyHelpers.js';
import defaultLogo from '../../../../assets/images/Wyenfos_bills_logo.png';
import watermark from '../../../../assets/images/watermark.png';
import cancelledWatermark from '../../../../assets/images/cancelled.png';
import './PaymentReceipt.css';

function PaymentReceipt() {
  const { state } = useLocation();
  const navigate = useNavigate();
  const initialReceiptId = state?.receiptId || null;
  const [customers, setCustomers] = useState([]);
  const [userRole, setUserRole] = useState('user');
  const [userId, setUserId] = useState(null);
  const [currentReceiptNumber, setCurrentReceiptNumber] = useState(1);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [requestChanges, setRequestChanges] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [creditBills, setCreditBills] = useState([]);
  const [debitNotes, setDebitNotes] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [selectedDebitNoteId, setSelectedDebitNoteId] = useState('');
  const [availableDebitNotes, setAvailableDebitNotes] = useState([]);
  const [selectedDebitNoteDetails, setSelectedDebitNoteDetails] = useState(null);
  const [companyDetails, setCompanyDetails] = useState(null);
  const [form, setForm] = useState({
    receiptNumber: 'PR-1',
    date: new Date().toISOString().split('T')[0],
    remarks: '',
    isCancelled: false,
    customer: {
      name: '',
      address: '',
      phone: '',
      email: '',
      gstin: ''
    },
    products: [
      {
        id: 1,
        name: '',
        quantity: 1,
        rate: 0,
        amount: 0
      }
    ],
    totalProductAmount: 0,
    totalPaidAmount: 0
  });
  const [receipts, setReceipts] = useState([]);
  const [showSideView, setShowSideView] = useState(!!initialReceiptId);
  const [highlightedReceiptId, setHighlightedReceiptId] = useState(initialReceiptId);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [showAllReceipts, setShowAllReceipts] = useState(false);


  const totalProductAmount = Array.isArray(creditBills)
    ? creditBills.reduce((sum, bill) => sum + (bill?.totalAmount || 0), 0)
    : 0;

  const totalPaidAmount = Array.isArray(debitNotes)
    ? debitNotes.reduce((sum, note) => sum + (note?.amountPaid || 0), 0)
    : 0;

  const remainingBalance = totalProductAmount - totalPaidAmount;

  // Display only latest 10 receipts by default, or all if showAllReceipts is true
  const displayedReceipts = showAllReceipts ? receipts : receipts.slice(0, 10);

  const validCompanies = [
    'WYENFOS INFOTECH',
    'WYENFOS GOLD & DIAMONDS',
    'WYENFOS ADS',
    'WYENFOS CASH VAPASE',
    'WYENFOS',
    'AYUR4LIFE HERBALS INDIA',
    'WYENFOS PURE DROPS',
    'WYENFOS CHARITABLE TRUST'
  ];

  // Handle company selection from URL parameters (similar to CreditNote and DebitNote)
  const [searchParams] = useSearchParams();
  const companyFromUrl = searchParams.get('company');
  
  let selectedCompanyFromState = 'WYENFOS';
  
  // Priority: URL parameter > location state > default
  if (companyFromUrl) {
    selectedCompanyFromState = companyFromUrl;
  } else if (state?.selectedCompany) {
    // If selectedCompany is an object, extract the name
    if (typeof state.selectedCompany === 'object' && state.selectedCompany.name) {
      selectedCompanyFromState = state.selectedCompany.name;
    } else if (typeof state.selectedCompany === 'string') {
      selectedCompanyFromState = state.selectedCompany;
    }
  }
  
  // Handle company name mapping for backward compatibility
  if (selectedCompanyFromState === 'AYUR 4 LIFE HERBALS India' || selectedCompanyFromState === 'AYUR4LIFE HERBALS INDIA') {
    selectedCompanyFromState = 'AYUR4LIFE HERBALS INDIA';
  }
  
  // Handle company name mapping for WYENFOS GOLD
  if (selectedCompanyFromState === 'WYENFOS GOLD & DIAMONDS') {
    selectedCompanyFromState = 'WYENFOS GOLD AND DIAMONDS';
  }
  
  // Ensure selectedCompanyFromState is always a string
  if (typeof selectedCompanyFromState !== 'string') {
    console.warn('selectedCompanyFromState is not a string:', selectedCompanyFromState);
    selectedCompanyFromState = 'WYENFOS';
  }

  // State for selected company (must be declared early to be used in functions below)
  const [selectedCompanyState, setSelectedCompanyState] = useState(selectedCompanyFromState);

  const defaultCompany = validCompanies.includes(
    typeof state?.selectedCompany === 'string' 
      ? state.selectedCompany.toUpperCase() 
      : (state?.selectedCompany?.name?.toUpperCase() || '')
  )
    ? (typeof state?.selectedCompany === 'string' ? state.selectedCompany : state?.selectedCompany?.name || 'WYENFOS')
    : 'WYENFOS';

  // Get company details using the helper function (similar to CreditBill)
  const selectedCompanyDetails = useMemo(() => {
    return getCompanyDetails(selectedCompanyState || selectedCompanyFromState);
  }, [selectedCompanyFromState, selectedCompanyState]);

  // Handle company change
  const handleCompanyChange = (e) => {
    const newCompany = e.target.value;
    setSelectedCompanyState(newCompany);
    
    // Update URL parameters
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('company', newCompany);
    navigate(`?${newSearchParams.toString()}`, { replace: true });
    
    // Reload the page to ensure all components use the new company
    window.location.reload();
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const decoded = jwtDecode(token);
        setUserRole(decoded.role || 'user');
        setUserId(decoded.id || decoded.sub || 'unknown');
        localStorage.setItem('userId', decoded.id || decoded.sub || 'unknown');
      } catch (error) {
        console.error('Error decoding token:', error);
      }
    }
  }, []);

  // Set default company details without API call
  useEffect(() => {
    const setDefaultCompanyDetails = () => {
      // Use hardcoded fallback companies instead of API call
      const fallbackCompanies = [
        {
          name: 'WYENFOS PURE DROPS',
          address: 'Ayur4Life Building, Thrissur, Kerala, 680001',
          logo: defaultLogo,
          GSTIN: '32AABCW1234F1Z5',
          email: 'info@wyenfospuredrops.com',
          mobile: '+91 9876543210',
          website: 'www.wyenfospuredrops.com',
          prefix: 'WPD'
        },
        {
          name: 'AYUR4LIFE HERBALS INDIA',
          address: 'Ayur4Life Building, Thrissur, Kerala, 680001',
          logo: defaultLogo,
          GSTIN: '32DEFGH5678I9J0',
          email: 'info@ayurherbals.com',
          mobile: '+91 9876543211',
          website: 'www.ayurherbals.com',
          prefix: 'ALH'
        },
        {
          name: 'WYENFOS INFOTECH',
          address: 'Thekkekara Arcade, Chelakottukara, Thrissur, Kerala, 680001',
          logo: defaultLogo,
          GSTIN: '32KLMNO1234P5Q6',
          email: 'info@wyenfosinfotech.com',
          mobile: '+91 9876543212',
          website: 'www.wyenfosinfotech.com',
          prefix: 'WIT'
        }
      ];

      const company = fallbackCompanies.find(c => 
        c.name?.toUpperCase() === defaultCompany.toUpperCase()
      ) || fallbackCompanies[0];
      
      setSelectedCompany(company);
    };

    setDefaultCompanyDetails();
  }, [defaultCompany]);

  const fetchCompanyDetails = useCallback(async (companyName) => {
    const currentCompany = companyName || selectedCompanyState || selectedCompanyFromState;
    
    // Use local fallback companies instead of API calls
    const fallbackCompanies = [
      {
        name: 'WYENFOS PURE DROPS',
        address: 'Ayur4Life Building, Thrissur, Kerala, 680001',
        logo: defaultLogo,
        GSTIN: '32AABCW1234F1Z5',
        email: 'info@wyenfospuredrops.com',
        mobile: '+91 9876543210',
        website: 'www.wyenfospuredrops.com',
        prefix: 'WPD'
      },
      {
        name: 'AYUR4LIFE HERBALS INDIA',
        address: 'Ayur4Life Building, Thrissur, Kerala, 680001',
        logo: defaultLogo,
        GSTIN: '32DEFGH5678I9J0',
        email: 'info@ayurherbals.com',
        mobile: '+91 9876543211',
        website: 'www.ayurherbals.com',
        prefix: 'ALH'
      },
      {
        name: 'WYENFOS INFOTECH',
        address: 'Thekkekara Arcade, Chelakottukara, Thrissur, Kerala, 680001',
        logo: defaultLogo,
        GSTIN: '32KLMNO1234P5Q6',
        email: 'info@wyenfosinfotech.com',
        mobile: '+91 9876543212',
        website: 'www.wyenfosinfotech.com',
        prefix: 'WIT'
      }
    ];

    const company = fallbackCompanies.find(c => 
      c.name && c.name.toLowerCase() === currentCompany.toLowerCase()
    ) || fallbackCompanies[0];
    
    if (company) {
      setCompanyDetails(company);
      return company;
    }
    
    setCompanyDetails(null);
    return null;
  }, [selectedCompanyFromState, selectedCompanyState]);

  const generateReceiptNumber = () => {
    // Use simple PR-n format for payment receipts with sequential numbering
    return `PR-${currentReceiptNumber}`;
  };


  // Product management functions
  const addProduct = () => {
    const newProduct = {
      id: Date.now(),
      name: '',
      quantity: 1,
      rate: 0,
      amount: 0
    };
    setForm(prev => ({
      ...prev,
      products: [...prev.products, newProduct]
    }));
  };

  const removeProduct = (productId) => {
    setForm(prev => ({
      ...prev,
      products: prev.products.filter(product => product.id !== productId)
    }));
    calculateTotals();
  };

  const updateProduct = (productId, field, value) => {
    setForm(prev => ({
      ...prev,
      products: prev.products.map(product => {
        if (product.id === productId) {
          const updatedProduct = { ...product, [field]: value };
          
          // Auto-calculate amount when quantity or rate changes
          if (field === 'quantity' || field === 'rate') {
            updatedProduct.amount = (updatedProduct.quantity || 0) * (updatedProduct.rate || 0);
          }
          
          return updatedProduct;
        }
        return product;
      })
    }));
    
    // Recalculate totals after a short delay to ensure state is updated
    setTimeout(calculateTotals, 100);
  };

  const calculateTotals = () => {
    setForm(prev => {
      const totalProductAmount = prev.products.reduce((sum, product) => {
        return sum + (product.amount || 0);
      }, 0);
      
      return {
        ...prev,
        totalProductAmount
      };
    });
  };

  useEffect(() => {
    const setInitialReceiptNumber = () => {
      // Start with receipt number 1
      setCurrentReceiptNumber(1);
      setForm(prev => ({
        ...prev,
        receiptNumber: 'PR-1'
      }));
    };

    setInitialReceiptNumber();
  }, []);

  // Calculate totals whenever products change
  useEffect(() => {
    calculateTotals();
  }, [form.products]);

  const handleRequestChange = async () => {
    // Check if user has super admin permission
    if (userRole !== 'super_admin') {
      setError('Access denied: Only Super Admin users can request changes');
      return;
    }

    if (!requestChanges.trim()) {
      setError('Please describe the changes you would like to request');
      return;
    }

    if (!highlightedReceiptId) {
      setError('Please select a receipt to request changes for');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        '/api/payment-receipts/request-change',
        {
          receiptId: highlightedReceiptId,
          changes: requestChanges,
          reason: 'Requested by Super Admin user'
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      console.log('Request change response:', response.data);
      setShowRequestModal(false);
      setRequestChanges('');
      setSuccessMessage('✅ Change request submitted successfully! Admin will review your request.');
      
      // Clear success message after 5 seconds
      setTimeout(() => {
        setSuccessMessage(null);
      }, 5000);
    } catch (error) {
      console.error('Request change error:', error);
      console.error('Error response:', error.response?.data);
      setError(error.response?.data?.message || 'Failed to submit change request. Please try again.');
    }
  };



  const fetchCustomers = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      console.log('Fetching customers with token:', token ? 'Token exists' : 'No token');
      
      const response = await axios.get('/api/customers', {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      console.log('API Response:', response);
      console.log('Fetched customers:', JSON.stringify(response.data, null, 2));
      
      // Handle different response formats
      let customersData = [];
      if (Array.isArray(response.data)) {
        customersData = response.data;
      } else if (response.data && Array.isArray(response.data.customers)) {
        // Handle {success: true, customers: []} format
        customersData = response.data.customers;
      } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
        // Handle nested data format
        customersData = response.data.data;
      } else {
        console.log('Response data in different format, using empty array:', response.data);
        customersData = [];
      }

      console.log('Setting customers array with length:', customersData.length);
      if (customersData.length > 0) {
        console.log('First customer sample:', customersData[0]);
        console.log('First customer keys:', Object.keys(customersData[0]));
      }
      setCustomers(customersData);
    } catch (err) {
      console.error('Error fetching customers:', err);
      console.error('Error details:', err.response?.data);
      setError('Failed to load customers. Please try again.');
      setCustomers([]);
    }
  }, []);

  const fetchCustomerBills = useCallback(async (customerId) => {
    try {
      const token = localStorage.getItem('token');
      console.log('Fetching complete customer data for ID:', customerId);
      
      // First fetch complete customer details
      let completeCustomerData = null;
      try {
        const customerResponse = await axios.get(`/api/customers/${customerId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        completeCustomerData = customerResponse.data;
        console.log('Complete customer data received:', completeCustomerData);
        console.log('Customer data keys:', Object.keys(completeCustomerData));
        console.log('Customer address:', completeCustomerData.address);
        console.log('Customer phone:', completeCustomerData.phone);
        console.log('Customer email:', completeCustomerData.email);
        console.log('Customer gstin:', completeCustomerData.gstin);
        console.log('Full customer object:', JSON.stringify(completeCustomerData, null, 2));
      } catch (customerError) {
        console.error('Error fetching complete customer data:', customerError);
        // Fallback: try to find customer in the existing customers array
        completeCustomerData = customers.find(c => (c._id === customerId) || (c.id === customerId));
        console.log('Using fallback customer data:', completeCustomerData);
      }
      
      // Update customer data immediately with complete information
      if (completeCustomerData) {
        // Check for alternative field names
        console.log('Checking for alternative field names...');
        console.log('Possible address fields:', {
          address: completeCustomerData.address,
          customerAddress: completeCustomerData.customerAddress,
          billingAddress: completeCustomerData.billingAddress,
          shippingAddress: completeCustomerData.shippingAddress
        });
        console.log('Possible phone fields:', {
          phone: completeCustomerData.phone,
          phoneNumber: completeCustomerData.phoneNumber,
          contactNumber: completeCustomerData.contactNumber,
          mobile: completeCustomerData.mobile
        });
        console.log('Possible email fields:', {
          email: completeCustomerData.email,
          emailAddress: completeCustomerData.emailAddress,
          contactEmail: completeCustomerData.contactEmail
        });
        console.log('Possible GSTIN fields:', {
          gstin: completeCustomerData.gstin,
          gstNumber: completeCustomerData.gstNumber,
          gst: completeCustomerData.gst
        });
        
        const updatedCustomerData = {
          _id: completeCustomerData._id || completeCustomerData.id,
          name: completeCustomerData.name || completeCustomerData.customerName || 'N/A',
          customerId: completeCustomerData.customerId || `CUST${(completeCustomerData._id || completeCustomerData.id)?.slice(-4) || '0000'}`,
          address: completeCustomerData.address || completeCustomerData.customerAddress || completeCustomerData.billingAddress || completeCustomerData.shippingAddress || completeCustomerData.customerContact?.address || '',
          phone: completeCustomerData.phone || completeCustomerData.phoneNumber || completeCustomerData.contactNumber || completeCustomerData.mobile || completeCustomerData.customerContact?.phone || '',
          email: completeCustomerData.email || completeCustomerData.emailAddress || completeCustomerData.contactEmail || completeCustomerData.customerContact?.email || '',
          gstin: completeCustomerData.gstin || completeCustomerData.gstNumber || completeCustomerData.gst || completeCustomerData.customerContact?.gstin || ''
        };
        
        setSelectedCustomer(updatedCustomerData);
        setForm(prev => ({
          ...prev,
          customer: {
            name: updatedCustomerData.name,
            address: updatedCustomerData.address,
            phone: updatedCustomerData.phone,
            email: updatedCustomerData.email,
            gstin: updatedCustomerData.gstin
          }
        }));
        
        console.log('Updated customer data with complete information:', updatedCustomerData);
      }
      
      // Then fetch bills and debit notes
      const [creditBillResponse, debitNoteResponse] = await Promise.all([
        axios.get(`/api/creditbills?customerId=${customerId}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`/api/debitnotes?customerId=${customerId}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      const creditBillsData = Array.isArray(creditBillResponse.data) ? creditBillResponse.data : [];
      const debitNotesData = Array.isArray(debitNoteResponse.data) ? debitNoteResponse.data : [];
      
      console.log('Raw credit bills data:', creditBillsData);
      console.log('Raw debit notes data:', debitNotesData);
      console.log('First debit note structure:', debitNotesData[0]);

      // Process credit bills to include payment status
      const processedCreditBills = creditBillsData.map(bill => {
        try {
          const relatedDebitNotes = debitNotesData.filter(note => note.creditBillId === bill._id);
          const totalPaid = relatedDebitNotes.reduce((sum, note) => sum + (note.amountPaid || 0), 0);
          const remainingBalance = (bill.totalAmount || 0) - totalPaid;
          const isFullyPaid = remainingBalance <= 0;

          return {
            ...bill,
            totalPaid,
            remainingBalance,
            isFullyPaid,
            paymentStatus: isFullyPaid ? 'Fully Paid' : 'Partially Paid',
            relatedDebitNotes
          };
        } catch (error) {
          console.error('Error processing credit bill:', error, bill);
          return {
            ...bill,
            totalPaid: 0,
            remainingBalance: bill.totalAmount || 0,
            isFullyPaid: false,
            paymentStatus: 'Error',
            relatedDebitNotes: []
          };
        }
      });

      // Create comprehensive payment history for each credit bill
      const paymentHistory = processedCreditBills.map(bill => {
        const history = [];
        
        // Add credit bill as first entry
        history.push({
          invoiceNumber: bill.creditBillNo || bill.invoiceNumber,
          billType: 'Credit Bill',
          paidAmount: bill.totalPaid || 0,
          remainingBalance: bill.remainingBalance,
          date: bill.date || bill.createdAt,
          description: 'Initial Payment',
          isCreditBill: true
        });

        // Add all related debit notes in chronological order
        const sortedDebitNotes = (bill.relatedDebitNotes || [])
          .sort((a, b) => new Date(a.date || a.createdAt) - new Date(b.date || b.createdAt));

        let runningBalance = bill.totalAmount || 0;
        
        sortedDebitNotes.forEach((note, index) => {
          const amountPaid = note.amountPaid || 0;
          runningBalance -= amountPaid;
          
          history.push({
            invoiceNumber: note.debitNoteNo || note.invoiceNumber,
            billType: 'Debit Note',
            paidAmount: amountPaid,
            remainingBalance: Math.max(0, runningBalance),
            date: note.date || note.createdAt,
            description: `Installment ${index + 1}`,
            isDebitNote: true,
            debitNoteId: note._id
          });
        });

        return {
          ...bill,
          paymentHistory,
          currentBalance: runningBalance
        };
      });

      console.log('Payment History:', paymentHistory);
      setCreditBills(processedCreditBills);
      setDebitNotes(debitNotesData);
      
      // Set available debit notes for selection
      const availableNotes = debitNotesData.map(note => ({
        _id: note._id || note.id, // Handle both _id and id fields
        debitNoteNo: note.debitNoteNo || note.invoiceNumber,
        amountPaid: note.amountPaid,
        date: note.date || note.createdAt,
        creditBillId: note.creditBillId
      }));
      
      console.log('Original debit notes data:', debitNotesData);
      console.log('Mapped available notes:', availableNotes);
      setAvailableDebitNotes(availableNotes);
      console.log('Available Debit Notes:', availableNotes);
      

    } catch (error) {
      console.error('Error fetching customer bills:', error.message);
      setError('Failed to fetch customer bills.');
      setCreditBills([]);
      setDebitNotes([]);
    }
  }, [customers]);

  useEffect(() => {
    console.log('Selected Customer State Changed:', selectedCustomer);
    console.log('Form Customer State:', form.customer);
    console.log('Form customer address:', form.customer?.address);
    console.log('Form customer phone:', form.customer?.phone);
    console.log('Form customer email:', form.customer?.email);
    console.log('Form customer gstin:', form.customer?.gstin);
  }, [selectedCustomer, form.customer]);

  useEffect(() => {
    console.log('Customers State Changed:', customers);
    console.log('Customers array length:', customers.length);
    
    // If we have a selected customer but missing details, try to update with complete data
    if (selectedCustomer && customers.length > 0) {
      const completeCustomer = customers.find(c => 
        (c._id === selectedCustomer._id) || (c.id === selectedCustomer._id) || (c._id === selectedCustomer.id) || (c.id === selectedCustomer.id)
      );
      
      console.log('Looking for complete customer data for ID:', selectedCustomer._id);
      console.log('Found complete customer:', completeCustomer);
      
      // Check if we need to update with more complete data
      const needsUpdate = completeCustomer && (
        (!selectedCustomer.address || selectedCustomer.address === '') ||
        (!selectedCustomer.phone || selectedCustomer.phone === '') ||
        (!selectedCustomer.email || selectedCustomer.email === '') ||
        (!selectedCustomer.gstin || selectedCustomer.gstin === '')
      ) && (
        completeCustomer.customerContact?.address ||
        completeCustomer.customerContact?.phone ||
        completeCustomer.customerContact?.email ||
        completeCustomer.customerContact?.gstin
      );
      
      if (needsUpdate) {
        console.log('Updating selected customer with complete data from customers array');
        console.log('Current selected customer:', selectedCustomer);
        console.log('Complete customer data:', completeCustomer);
        
        const updatedCustomerData = {
          _id: completeCustomer._id || completeCustomer.id,
          name: completeCustomer.name || completeCustomer.customerName || selectedCustomer.name,
          customerId: completeCustomer.customerId || selectedCustomer.customerId,
          address: completeCustomer.address || completeCustomer.customerAddress || completeCustomer.billingAddress || completeCustomer.shippingAddress || completeCustomer.customerContact?.address || selectedCustomer.address || '',
          phone: completeCustomer.phone || completeCustomer.phoneNumber || completeCustomer.contactNumber || completeCustomer.mobile || completeCustomer.customerContact?.phone || selectedCustomer.phone || '',
          email: completeCustomer.email || completeCustomer.emailAddress || completeCustomer.contactEmail || completeCustomer.customerContact?.email || selectedCustomer.email || '',
          gstin: completeCustomer.gstin || completeCustomer.gstNumber || completeCustomer.gst || completeCustomer.customerContact?.gstin || selectedCustomer.gstin || ''
        };
        
        console.log('Complete customer object keys:', Object.keys(completeCustomer));
        console.log('Complete customer full object:', JSON.stringify(completeCustomer, null, 2));
        
        console.log('Updated customer data:', updatedCustomerData);
        
        setSelectedCustomer(updatedCustomerData);
        setForm(prev => ({
          ...prev,
          customer: {
            name: updatedCustomerData.name,
            address: updatedCustomerData.address,
            phone: updatedCustomerData.phone,
            email: updatedCustomerData.email,
            gstin: updatedCustomerData.gstin
          }
        }));
      }
    }
  }, [customers, selectedCustomer]);

  const fetchReceipts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Use empty array as fallback since API is not available
      const sortedReceipts = [];
      setReceipts(sortedReceipts);
      console.log('Receipts initialized with empty array');
    } catch (error) {
      console.error('Error initializing receipts:', error);
      setError('Failed to load receipts. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const setInitialReceiptNumber = () => {
      // Set initial receipt number without API call
      setCurrentReceiptNumber(1);
    };

    setInitialReceiptNumber();
    fetchCustomers();
    fetchReceipts();
    
    // Fetch company details if selectedCompany has a name
    if (selectedCompany && selectedCompany.name) {
      fetchCompanyDetails(selectedCompany.name);
    }
    
    // Also fetch company details for the selected company state
    if (selectedCompanyState || selectedCompanyFromState) {
      fetchCompanyDetails(selectedCompanyState || selectedCompanyFromState);
    }
  }, [fetchCustomers, fetchReceipts, fetchCompanyDetails, selectedCompany, selectedCompanyState, selectedCompanyFromState]);

 const handleCustomerSelect = (e) => {
  const customerId = e.target.value;
  
  if (!customerId) {
    setSelectedCustomer(null);
    setCreditBills([]);
    setDebitNotes([]);
    setAvailableDebitNotes([]);
    setSelectedDebitNoteId('');
    setForm(prev => ({
      ...prev,
      customer: {
        name: '',
        address: '',
        phone: '',
        email: '',
        gstin: ''
      }
    }));
    return;
  }

  const customer = customers.find(c => (c._id === customerId) || (c.id === customerId));
  
  if (customer) {
    const customerData = {
      _id: customer._id || customer.id,
      name: customer.name || customer.customerName || 'N/A',
      customerId: customer.customerId || `CUST${(customer._id || customer.id)?.slice(-4) || '0000'}`,
      address: customer.address || customer.customerAddress || customer.billingAddress || customer.shippingAddress || customer.customerContact?.address || '',
      phone: customer.phone || customer.phoneNumber || customer.contactNumber || customer.mobile || customer.customerContact?.phone || '',
      email: customer.email || customer.emailAddress || customer.contactEmail || customer.customerContact?.email || '',
      gstin: customer.gstin || customer.gstNumber || customer.gst || customer.customerContact?.gstin || ''
    };

    setSelectedCustomer(customerData);
    
    setForm(prev => ({
      ...prev,
      customer: {
        name: customerData.name,
        address: customerData.address,
        phone: customerData.phone,
        email: customerData.email,
        gstin: customerData.gstin
      }
    }));

    if (customer._id || customer.id) {
      const customerId = customer._id || customer.id;
      fetchCustomerBills(customerId);
    }
  } else {
    setSelectedCustomer(null);
    setCreditBills([]);
    setDebitNotes([]);
    setAvailableDebitNotes([]);
    setSelectedDebitNoteId('');
    setForm(prev => ({
      ...prev,
      customer: {
        name: '',
        address: '',
        phone: '',
        email: '',
        gstin: ''
      }
    }));
  }
};



  const handleCustomerDetailChange = (e) => {
    const { name, value } = e.target;
    setSelectedCustomer(prev => ({
      ...prev,
      [name]: value
    }));
    setForm(prev => ({
      ...prev,
      customer: {
        ...prev.customer,
        [name]: value
      }
    }));
  };

  const fetchDebitNoteDetails = useCallback(async (debitNoteId) => {
    try {
      const token = localStorage.getItem('token');
      console.log('Fetching debit note details for ID:', debitNoteId);
      
      const response = await axios.get(`/api/debitnotes/${debitNoteId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const debitNoteDetails = response.data;
      console.log('Debit note details received:', debitNoteDetails);
      console.log('Debit note details keys:', Object.keys(debitNoteDetails));
      console.log('Amount fields check:', {
        totalAmount: debitNoteDetails.totalAmount,
        grandTotal: debitNoteDetails.grandTotal,
        amountPaid: debitNoteDetails.amountPaid,
        paidAmount: debitNoteDetails.paidAmount,
        totalPaid: debitNoteDetails.totalPaid,
        amount: debitNoteDetails.amount
      });
      
      setSelectedDebitNoteDetails(debitNoteDetails);
      return debitNoteDetails;
    } catch (error) {
      console.error('Error fetching debit note details:', error);
      // Fallback: try to find in available debit notes
      const fallbackNote = availableDebitNotes.find(note => note._id === debitNoteId);
      if (fallbackNote) {
        console.log('Using fallback debit note data:', fallbackNote);
        setSelectedDebitNoteDetails(fallbackNote);
        return fallbackNote;
      }
      return null;
    }
  }, [availableDebitNotes]);

  const handleDebitNoteSelect = async (debitNoteId) => {
    setSelectedDebitNoteId(debitNoteId);
    if (debitNoteId) {
      console.log('Debit note selected:', debitNoteId);
      // Fetch detailed debit note information
      const debitNoteDetails = await fetchDebitNoteDetails(debitNoteId);
      if (debitNoteDetails) {
        console.log('Debit note details loaded:', debitNoteDetails);
      }
    } else {
      // Clear debit note details when no debit note is selected
      setSelectedDebitNoteDetails(null);
    }
  };

  const saveReceipt = async () => {
    if (!selectedCustomer) {
      setError('Please select a customer.');
      return;
    }
    if (!selectedCustomer._id) {
      setError('Customer ID is required.');
      return;
    }
    
    // More flexible ID validation - allow both MongoDB ObjectId (24 chars) and Firestore IDs (20 chars)
    const idRegex = /^[0-9a-zA-Z]{20,24}$/;
    if (!idRegex.test(selectedCustomer._id)) {
      console.log('Customer ID validation failed:', selectedCustomer._id);
      console.log('Customer ID length:', selectedCustomer._id.length);
      setError('Invalid customer ID format.');
      return;
    }
    // Calculate amounts based on whether a debit note is selected
    const finalTotalProductAmount = selectedDebitNoteId && selectedDebitNoteDetails 
      ? (selectedDebitNoteDetails.totalAmount || selectedDebitNoteDetails.grandTotal || 0)
      : totalProductAmount;
    
    const finalTotalPaidAmount = selectedDebitNoteId && selectedDebitNoteDetails
      ? (selectedDebitNoteDetails.amountPaid || 0)
      : totalPaidAmount;

    // Validate that amounts are greater than zero
    if (!form.totalPaidAmount || form.totalPaidAmount <= 0) {
      setError('Total paid amount must be greater than zero.');
      return;
    }

    if (!finalTotalProductAmount || finalTotalProductAmount <= 0) {
      setError('Total product amount must be greater than zero.');
      return;
    }

    // Validate customer details
    if (!selectedCustomer.name || selectedCustomer.name.trim() === '') {
      setError('Customer name is required.');
      return;
    }
    
    // Validate email and mobile for receipt delivery
    const customerEmail = selectedCustomer?.email || form.customer.email;
    if (!customerEmail || customerEmail.trim() === '') {
      setError('Email address is required for receipt delivery.');
      return;
    }
    
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(customerEmail)) {
      setError('Please enter a valid email address.');
      return;
    }
    
    if (!selectedCustomer.phone || selectedCustomer.phone.trim() === '') {
      setError('Mobile number is required for receipt delivery.');
      return;
    }
    
    // Basic mobile validation (at least 10 digits)
    const phoneRegex = /^\d{10,}$/;
    if (!phoneRegex.test(selectedCustomer.phone.replace(/\D/g, ''))) {
      setError('Please enter a valid mobile number (at least 10 digits).');
      return;
    }

    // Validate that the amount paid equals the total product amount
    if (Math.abs(finalTotalPaidAmount - finalTotalProductAmount) > 0.01) {
      setError(`Payment receipt can only be generated for full payment. Amount paid (₹${finalTotalPaidAmount.toFixed(2)}) must equal total product amount (₹${finalTotalProductAmount.toFixed(2)}).`);
      return;
    }

    setLoading(true);
    setError(null);

    const receipt = {
      receiptNo: generateReceiptNumber(),
      receiptNumber: generateReceiptNumber(), // Add this for consistency
      customerId: selectedCustomer.customerId || selectedCustomer._id,
      customerName: form.customer.name || selectedCustomer.name || 'N/A',
      amount: finalTotalPaidAmount,
      date: new Date(form.date),
      type: 'payment',
      creditBillId: creditBills.length > 0 ? creditBills[0]._id : undefined,
      debitNoteId: selectedDebitNoteId || undefined,
      metadata: {
        creditBills: creditBills.map(bill => ({
          _id: bill._id,
          creditBillNo: bill.creditBillNo,
          date: new Date(bill.date),
          totalAmount: bill.totalAmount
        })),
        debitNotes: debitNotes.map(note => ({
          _id: note._id,
          debitNoteNo: note.debitNoteNo,
          date: new Date(note.date),
          amountPaid: note.amountPaid
        })),
        totalProductAmount: finalTotalProductAmount,
        totalPaidAmount: finalTotalPaidAmount,
        remainingBalance,
        remarks: form.remarks,
        email: selectedCustomer.email || form.customer.email,
        phone: selectedCustomer.phone || form.customer.phone,
        receiptDelivery: {
          email: selectedCustomer.email || form.customer.email,
          mobile: selectedCustomer.phone || form.customer.phone,
          deliveryMethod: 'email_and_sms'
        }
      },
      createdBy: userId || 'unknown',
      lastUpdatedBy: userId || 'unknown'
    };

    console.log('Receipt payload:', JSON.stringify(receipt, null, 2));

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post('/api/payment-receipts', receipt, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      setReceipts([...receipts, response.data]);
      setCurrentReceiptNumber(prev => {
        const nextNumber = prev + 1;
        setForm(prevForm => ({
          ...prevForm,
          receiptNumber: `PR-${nextNumber}`
        }));
        return nextNumber;
      });
      setError(null);
      
      // Check if email delivery was requested
      const emailDelivery = response.data.metadata?.receiptDelivery?.email;
      if (emailDelivery) {
        setSuccessMessage(`✅ Payment receipt saved successfully!\n\n📧 Email receipt sent to: ${emailDelivery}\n📱 Mobile: ${selectedCustomer.phone}\n\n📄 Receipt Number: ${response.data.receiptNumber}\n💰 Amount: ₹${response.data.amount.toFixed(2)}`);
      } else {
        setSuccessMessage(`✅ Payment receipt saved successfully!\n\n📧 Email: ${selectedCustomer.email}\n📱 Mobile: ${selectedCustomer.phone}\n\n📄 Receipt Number: ${response.data.receiptNumber}\n💰 Amount: ₹${response.data.amount.toFixed(2)}`);
      }
      
      // Clear success message after 8 seconds
      setTimeout(() => {
        setSuccessMessage(null);
      }, 8000);
    } catch (error) {
      console.error('Error saving receipt:', error.response?.data || error);
      setError(error.response?.data?.message || 'Failed to save payment receipt.');
    } finally {
      setLoading(false);
    }
  };

  const loadImage = (src) => {
    return new Promise((resolve, reject) => {
      if (!src) {
        // Try to load default logo instead
        const img = new Image();
        img.src = defaultLogo;
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error('No image source provided and default logo failed'));
        return;
      }
      
      const img = new Image();
      img.src = src;
      img.onload = () => resolve(img);
      img.onerror = () => {
        console.error(`Failed to load image: ${src}, trying default logo`);
        // Try default logo as fallback
        const fallbackImg = new Image();
        fallbackImg.src = defaultLogo;
        fallbackImg.onload = () => resolve(fallbackImg);
        fallbackImg.onerror = () => {
          console.error('Default logo also failed to load');
          reject(new Error(`Failed to load image: ${src} and fallback failed`));
        };
      };
    });
  };

  const generatePrintFormatPDF = async (receipt = {
    receiptNo: form.receiptNumber,
    date: form.date,
    customer: selectedCustomer,
    creditBills,
    debitNotes,
    totalProductAmount: selectedDebitNoteId && selectedDebitNoteDetails 
      ? (selectedDebitNoteDetails.totalAmount || selectedDebitNoteDetails.grandTotal || 0)
      : totalProductAmount,
    totalPaidAmount: selectedDebitNoteId && selectedDebitNoteDetails
      ? (selectedDebitNoteDetails.amountPaid || 0)
      : totalPaidAmount,
    remainingBalance: selectedDebitNoteId && selectedDebitNoteDetails
      ? ((selectedDebitNoteDetails.totalAmount || selectedDebitNoteDetails.grandTotal || 0) - (selectedDebitNoteDetails.amountPaid || 0))
      : remainingBalance,
    remarks: form.remarks,
    selectedDebitNoteDetails
  }) => {
    try {
      // Create PDF with compression settings
      const doc = new jsPDF('p', 'mm', 'a4', {
        compress: true,
        precision: 16
      });
      doc.setFontSize(12);
      let y = 10;

      // Load and compress logo with proper fallback
      let logoImg = null;
      try {
        const logoUrl = getLogoUrl(
          selectedCompanyDetails?.logo || selectedCompany?.logo, 
          selectedCompanyDetails?.name || selectedCompany?.name || selectedCompanyState || selectedCompanyFromState || 'WYENFOS'
        );
        logoImg = await loadImage(logoUrl);
      } catch (logoError) {
        console.error('Logo loading failed, continuing without logo:', logoError);
        // Continue PDF generation without logo
      }
      
      // Debug logo loading
      console.log('Logo Debug:', {
        companyDetailsLogo: companyDetails?.logo,
        selectedCompanyLogo: selectedCompany?.logo,
        selectedCompanyDetailsLogo: selectedCompanyDetails?.logo,
        defaultLogo: wyenfosLogo,
        finalLogo: selectedCompanyDetails?.logo || wyenfosLogo
      });

      // Add compressed logo with smaller size (only if logo loaded successfully)
      let companyInfoX = 10; // Default position if no logo
      let logoHeight = 20; // Default height for layout calculations
      
      if (logoImg) {
        const logoWidth = 20; // Reduced from 25
        logoHeight = (logoImg.height / logoImg.width) * logoWidth;
        doc.addImage(logoImg, 'JPEG', 10, y, logoWidth, logoHeight, undefined, 'FAST', 0.8); // Use JPEG compression
        companyInfoX = 10 + logoWidth + 5; // 5px gap after logo
      }
      
      // Company Information - positioned on left side (next to logo if available)
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(14);
      doc.text(selectedCompanyDetails?.name || companyDetails?.name || selectedCompany?.name || selectedCompanyState || selectedCompanyFromState || 'WYENFOS BILLS', companyInfoX, y + 5);
      
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.text(selectedCompanyDetails?.address || companyDetails?.address || selectedCompany?.address || 'Thekkekara Arcade, Chelakottukara, Thrissur, Kerala, 680001', companyInfoX, y + 10);
      
      if (selectedCompanyDetails?.GSTIN || companyDetails?.GSTIN || selectedCompany?.GSTIN) {
        doc.text(`GSTIN: ${selectedCompanyDetails?.GSTIN || companyDetails?.GSTIN || selectedCompany?.GSTIN}`, companyInfoX, y + 14);
      }
      
      if (selectedCompanyDetails?.email || companyDetails?.email || selectedCompany?.email) {
        doc.text(`Email: ${selectedCompanyDetails?.email || companyDetails?.email || selectedCompany?.email}`, companyInfoX, y + 18);
      }
      
      if (selectedCompanyDetails?.mobile || companyDetails?.mobile || selectedCompany?.mobile) {
        doc.text(`Phone: ${selectedCompanyDetails?.mobile || companyDetails?.mobile || selectedCompany?.mobile}`, companyInfoX, y + 22);
      }
      
      // Payment Receipt Title and Details - positioned closer to red line (right edge)
      const receiptDetailsX = 200; // Move closer to red line/page edge
      
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(16);
      const receiptTitle = form.isCancelled ? 'PAYMENT RECEIPT (CANCELLED)' : 'PAYMENT RECEIPT';
      doc.text(receiptTitle, receiptDetailsX, y + 5, { align: 'right' });
      
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.text(`Receipt No: ${receipt.receiptNo}`, receiptDetailsX, y + 10, { align: 'right' });
      
      // Date display matching print format
      const displayDate = receipt.date ? new Date(receipt.date).toLocaleDateString('en-IN') : new Date().toLocaleDateString('en-IN');
      doc.text(`Date: ${displayDate}`, receiptDetailsX, y + 14, { align: 'right' });
      
      // Set y position to the bottom of the company header section
      y += Math.max(logoHeight, 30) + 5;
      y += 12;

      // Customer Information - Two Column Layout
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('Customer Details:', 10, y);
      y += 8;
      
      // Customer details arranged in pairs
      doc.setFont('helvetica', 'normal');
      const customerName = receipt.customer?.name || selectedCustomer?.name || form.customer?.name || 'N/A';
      const customerId = receipt.customer?.customerId || receipt.customer?._id || selectedCustomer?.customerId || selectedCustomer?._id || 'N/A';
      const customerAddress = receipt.customer?.address || selectedCustomer?.address || form.customer?.address || 'N/A';
      const customerPhone = receipt.customer?.phone || selectedCustomer?.phone || form.customer?.phone || 'N/A';
      const customerEmail = receipt.customer?.email || selectedCustomer?.email || form.customer?.email || 'N/A';
      const customerGstin = receipt.customer?.gstin || selectedCustomer?.gstin || form.customer?.gstin || 'N/A';
      
      // Right side position for paired details
      const rightColumnX = 140;
      
      // Line 1: Customer Name and Phone
      doc.text(`Customer Name: ${customerName}`, 10, y);
      doc.text(`Phone: ${customerPhone}`, rightColumnX, y);
      y += 6;
      
      // Line 2: Customer ID and Email
      doc.text(`Customer ID: ${customerId}`, 10, y);
      doc.text(`Email: ${customerEmail}`, rightColumnX, y);
      y += 6;
      
      // Line 3: Address and GSTIN
      doc.text(`Address: ${customerAddress}`, 10, y);
      doc.text(`GSTIN: ${customerGstin}`, rightColumnX, y);
      
      y += 10;

      // Product Details Table
      console.log('PDF Debug - Products:', form.products);
      console.log('PDF Debug - Products length:', form.products?.length);
      
      doc.setFont('helvetica', 'bold');
      doc.text('Product Details:', 10, y);
      y += 8;

      // Create product table - always show table, even if empty
      let productTableData = [];
      
      if (form.products && form.products.length > 0) {
        productTableData = form.products
          .filter(product => product.name && product.name.trim()) // Only include products with names
          .map((product, index) => [
            index + 1,
            product.name || '',
            product.quantity || 1,
            `${(product.rate || 0).toFixed(2)}`,
            `${(product.amount || 0).toFixed(2)}`
          ]);
      }
      
      // If no products with names, show empty row
      if (productTableData.length === 0) {
        productTableData = [['1', 'No products added', '0', '0.00', '0.00']];
      }

      console.log('PDF Debug - Product table data:', productTableData);

      // Draw table manually for better control - extend to red line
      const tableStartX = 10;
      const tableEndX = 200; // Extend table to near the red line (page edge)
      const totalTableWidth = tableEndX - tableStartX; // 190mm total width
      const colWidths = [25, 90, 25, 25, 25]; // Redistributed widths
      const colPositions = [
        tableStartX,                           // S.No: 10
        tableStartX + colWidths[0],            // Product Name: 35
        tableStartX + colWidths[0] + colWidths[1], // Qty: 125
        tableStartX + colWidths[0] + colWidths[1] + colWidths[2], // Rate: 150
        tableStartX + colWidths[0] + colWidths[1] + colWidths[2] + colWidths[3] // Amount: 175
      ];
      const tableWidth = totalTableWidth; // 190mm - extends to red line
      
      // Draw table header
      doc.setFillColor(155, 119, 142);
      doc.rect(10, y, tableWidth, 8, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      
      const headers = ['S.No', 'Product Name', 'Qty', 'Rate', 'Amount'];
      headers.forEach((header, i) => {
        const align = i === 0 || i === 2 ? 'center' : i === 1 ? 'left' : 'right';
        let xPos;
        
        if (align === 'center') {
          xPos = colPositions[i] + colWidths[i] / 2;
        } else if (align === 'right') {
          xPos = colPositions[i] + colWidths[i] - 3; // 3mm padding from right edge
        } else {
          xPos = colPositions[i] + 3; // 3mm padding from left edge
        }
        
        doc.text(header, xPos, y + 6, { align });
      });
      y += 8;
      
      // Draw table rows
      doc.setTextColor(0, 0, 0);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      
      productTableData.forEach((row, rowIndex) => {
        // Draw row background (alternating)
        if (rowIndex % 2 === 1) {
          doc.setFillColor(248, 249, 250);
          doc.rect(10, y, tableWidth, 6, 'F');
        }
        
        // Draw row borders
        doc.setDrawColor(0, 0, 0);
        doc.setLineWidth(0.1);
        doc.rect(10, y, tableWidth, 6);
        
        // Draw cell content with proper positioning
        row.forEach((cell, i) => {
          const align = i === 0 || i === 2 ? 'center' : i === 1 ? 'left' : 'right';
          let xPos;
          
          if (align === 'center') {
            xPos = colPositions[i] + colWidths[i] / 2;
          } else if (align === 'right') {
            xPos = colPositions[i] + colWidths[i] - 3; // 3mm padding from right edge
          } else {
            xPos = colPositions[i] + 3; // 3mm padding from left edge
          }
          
          doc.text(String(cell), xPos, y + 4, { align });
          
          // Draw vertical lines between columns
          if (i < headers.length - 1) {
            doc.setDrawColor(0, 0, 0);
            doc.setLineWidth(0.1);
            doc.line(colPositions[i] + colWidths[i], y, colPositions[i] + colWidths[i], y + 6);
          }
        });
        y += 6;
      });
      
      y += 10;

      // Payment Details - Simplified Layout
      doc.setFont('helvetica', 'bold');
      doc.text('Payment Details:', 10, y);
      y += 8;
      
      // Show credit bill information
      if (receipt.creditBills && receipt.creditBills.length > 0) {
        doc.setFont('helvetica', 'normal');
        doc.text(`Credit Bill Number: ${receipt.creditBills[0]?.creditBillNo || receipt.creditBills[0]?.invoiceNumber || 'N/A'}`, 10, y);
        y += 6;
      }
      
      // Show debit note information if available
      if (receipt.selectedDebitNoteDetails) {
        const note = receipt.selectedDebitNoteDetails;
        doc.text(`Debit Note Number: ${note.debitNoteNo || note.invoiceNumber || 'N/A'}`, 10, y);
        y += 6;
        doc.text(`Payment Date: ${note.date ? new Date(note.date).toLocaleDateString('en-IN') : 'N/A'}`, 10, y);
        y += 6;
      } else if (receipt.debitNotes && receipt.debitNotes.length > 0) {
        receipt.debitNotes.forEach((note, index) => {
          doc.text(`Debit Note ${index + 1}: ${note.debitNoteNo || 'N/A'}`, 10, y);
          y += 6;
          doc.text(`Payment Date: ${note.date ? new Date(note.date).toLocaleDateString('en-IN') : 'N/A'}`, 10, y);
          y += 6;
        });
      }

      y += 15;
      
      // Total Amount Details - Right Side without border, with equal padding
      const rightSideX = 140; // Start position for right-aligned totals
      const pageRightMargin = 200; // Right edge position
      
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.setTextColor(0, 0, 0);
      
      // Add spacing before totals
      y += 5;
      
      // Total Product Amount
      doc.text(`Total Product Amount:`, rightSideX, y);
      const totalProductAmount = receipt.totalProductAmount || form.totalProductAmount || 0;
      doc.text(`${totalProductAmount.toFixed(2)}`, pageRightMargin, y, { align: 'right' });
      y += 7;
      
      // Total Paid Amount
      doc.text(`Total Paid Amount:`, rightSideX, y);
      const totalPaidAmount = receipt.totalPaidAmount || form.totalPaidAmount || 0;
      doc.text(`${totalPaidAmount.toFixed(2)}`, pageRightMargin, y, { align: 'right' });
      y += 7;
      
      // Balance with color coding
      const balance = totalProductAmount - totalPaidAmount;
      doc.text(`Balance:`, rightSideX, y);
      
      // Color code the balance
      if (balance <= 0) {
        doc.setTextColor(0, 128, 0); // Green for fully paid
      } else {
        doc.setTextColor(220, 53, 69); // Red for pending
      }
      doc.text(`${balance.toFixed(2)}`, pageRightMargin, y, { align: 'right' });
      doc.setTextColor(0, 0, 0); // Reset to black
      y += 10;
      
      // Add green "All outstanding bills settled" message when fully paid
      if (receipt.remainingBalance <= 0) {
        doc.setTextColor(0, 128, 0); // Green color
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(12);
        doc.text('All outstanding bills have been settled', 105, y,{align:"center"});
        y += 8;
        doc.setTextColor(0, 0, 0); // Reset to black
      }
      
      // Add cancelled watermark if receipt is cancelled
      if (form.isCancelled) {
        doc.setTextColor(220, 53, 69); // Red color for cancelled
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(20);
        doc.text('CANCELLED', 105, y, { align: 'center' });
        y += 15;
        doc.setTextColor(0, 0, 0); // Reset to black
      }
      
      // Add cancelled watermark if receipt is cancelled
      if (form.isCancelled) {
        // Save current graphics state
        doc.saveGraphicsState();
        
        // Set watermark properties
        doc.setTextColor(220, 53, 69); // Red color
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(48);
        
        // Calculate center position
        const pageWidth = 210; // A4 width in mm
        const pageHeight = 297; // A4 height in mm
        const centerX = pageWidth / 2;
        const centerY = pageHeight / 2;
        
        // Set rotation and transparency for watermark effect
        doc.setGState(new doc.GState({ opacity: 0.3 })); // 30% opacity
        
        // Draw diagonal "CANCELLED" watermark
        doc.text('CANCELLED', centerX, centerY, { 
          align: 'center',
          angle: -45 // 45 degree rotation
        });
        
        // Restore graphics state
        doc.restoreGraphicsState();
      }
      
      // Thank You Message
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
      doc.text('Thank You for Your Business!', 105, y, { align: 'center' });
      y += 8;
      
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.text('We appreciate your trust and look forward to serving you again.', 105, y, { align: 'center' });
      y += 20;

      // Check if we need a page break for signature
      if (y > 250) {
        doc.addPage();
        y = 20;
      }
      
      y += 20;
      
      return doc;
    } catch (error) {
      console.error('Error generating print format PDF:', error);
      throw error;
    }
  };

  const downloadPDF = async (receipt = {
    receiptNo: form.receiptNumber,
    date: form.date,
    customer: selectedCustomer,
    creditBills,
    debitNotes,
    totalProductAmount: selectedDebitNoteId && selectedDebitNoteDetails 
      ? (selectedDebitNoteDetails.totalAmount || selectedDebitNoteDetails.grandTotal || 0)
      : totalProductAmount,
    totalPaidAmount: selectedDebitNoteId && selectedDebitNoteDetails
      ? (selectedDebitNoteDetails.amountPaid || 0)
      : totalPaidAmount,
    remainingBalance: selectedDebitNoteId && selectedDebitNoteDetails
      ? ((selectedDebitNoteDetails.totalAmount || selectedDebitNoteDetails.grandTotal || 0) - (selectedDebitNoteDetails.amountPaid || 0))
      : remainingBalance,
    remarks: form.remarks,
    selectedDebitNoteDetails
  }) => {
    try {
      const doc = await generatePrintFormatPDF(receipt);
      doc.save(`Payment_Receipt_${receipt.receiptNo}.pdf`);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      setError('Failed to generate PDF.');
    }
  };

  const printReceipt = async () => {
    try {
      // Generate PDF first using the same function as download
      const receipt = {
        receiptNo: form.receiptNumber,
        date: form.date,
        customer: selectedCustomer,
        creditBills,
        debitNotes,
        totalProductAmount: selectedDebitNoteId && selectedDebitNoteDetails 
          ? (selectedDebitNoteDetails.totalAmount || selectedDebitNoteDetails.grandTotal || 0)
          : totalProductAmount,
        totalPaidAmount: selectedDebitNoteId && selectedDebitNoteDetails
          ? (selectedDebitNoteDetails.amountPaid || 0)
          : totalPaidAmount,
        remainingBalance: selectedDebitNoteId && selectedDebitNoteDetails
          ? ((selectedDebitNoteDetails.totalAmount || selectedDebitNoteDetails.grandTotal || 0) - (selectedDebitNoteDetails.amountPaid || 0))
          : remainingBalance,
        remarks: form.remarks,
        selectedDebitNoteDetails
      };

      const doc = await generatePrintFormatPDF(receipt);
      
      // Convert PDF to blob and create object URL
      const pdfBlob = doc.output('blob');
      const pdfUrl = URL.createObjectURL(pdfBlob);
      
      // Create a new window with the PDF and trigger print
      const printWindow = window.open(pdfUrl, '_blank');
      
      // Wait for the PDF to load, then trigger print
      printWindow.onload = () => {
        setTimeout(() => {
          printWindow.print();
        }, 500); // Small delay to ensure PDF is fully loaded
      };
      
      // Clean up the object URL after a delay
      setTimeout(() => {
        URL.revokeObjectURL(pdfUrl);
      }, 10000);
      
    } catch (error) {
      console.error('Error generating PDF for print:', error);
      setError('Failed to generate PDF for printing. Please try again.');
    }
  };

  const sendEmail = async () => {
    const customerEmail = selectedCustomer?.email || form.customer.email;
    if (!customerEmail) {
      setError('No customer email available.');
      return;
    }

    setLoading(true);
    setSuccessMessage('🔄 Generating PDF and preparing email...');
    
    try {
      const token = localStorage.getItem('token');
      
      try {
        await axios.get('/api/payment-receipts/last', {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
      } catch (serverError) {
        console.error('Server connection error:', serverError);
        setError('Server connection failed. Please ensure the server is running and try again.');
        setLoading(false);
        return;
      }
      
      // Define tempReceipt in the proper scope
      let tempReceipt;
      
      if (highlightedReceiptId) {
        // For existing receipts, get the receipt data
        const receipt = receipts.find(r => r._id === highlightedReceiptId);
        if (!receipt) {
          setError('Selected receipt not found. Please refresh and try again.');
          setLoading(false);
          return;
        }
        
        // Create tempReceipt for existing receipts
        tempReceipt = {
          receiptNo: receipt.receiptNo,
          receiptNumber: receipt.receiptNo,
          customerId: receipt.customerId,
          customerName: receipt.customerName,
          amount: receipt.amount,
          date: new Date(receipt.date),
          metadata: receipt.metadata || {}
        };
      } else {
        // For new receipts, generate PDF on frontend first
        setSuccessMessage('📄 Generating PDF...');
        
        tempReceipt = {
          receiptNo: form.receiptNumber,
          receiptNumber: form.receiptNumber,
          customerId: selectedCustomer.customerId || selectedCustomer._id,
          customerName: selectedCustomer.name,
          amount: selectedDebitNoteDetails?.amountPaid || totalPaidAmount,
          date: new Date(form.date),
          metadata: {
            totalProductAmount: selectedDebitNoteDetails?.totalAmount || totalProductAmount,
            totalPaidAmount: selectedDebitNoteDetails?.amountPaid || totalPaidAmount,
            remarks: form.remarks
          }
        };

        // Generate PDF using the same function as download/print
        const receiptForPDF = {
          receiptNo: form.receiptNumber,
          date: form.date,
          customer: selectedCustomer,
          creditBills,
          debitNotes,
          totalProductAmount: selectedDebitNoteId && selectedDebitNoteDetails 
            ? (selectedDebitNoteDetails.totalAmount || selectedDebitNoteDetails.grandTotal || 0)
            : totalProductAmount,
          totalPaidAmount: selectedDebitNoteId && selectedDebitNoteDetails
            ? (selectedDebitNoteDetails.amountPaid || 0)
            : totalPaidAmount,
          remainingBalance: selectedDebitNoteId && selectedDebitNoteDetails
            ? ((selectedDebitNoteDetails.totalAmount || selectedDebitNoteDetails.grandTotal || 0) - (selectedDebitNoteDetails.amountPaid || 0))
            : remainingBalance,
          remarks: form.remarks,
          selectedDebitNoteDetails
        };

        // Generate PDF with timeout
        const pdfPromise = generatePrintFormatPDF(receiptForPDF);
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('PDF generation timed out')), 10000)
        );
        
        const doc = await Promise.race([pdfPromise, timeoutPromise]);
        
        // Compress PDF and check size
        const pdfBase64 = doc.output('datauristring').split(',')[1]; // Get base64 without data URI prefix
        
        // Check PDF size before sending
        const pdfSizeMB = (pdfBase64.length * 3) / 4 / (1024 * 1024); // Approximate size in MB
        console.log('PDF Size:', pdfSizeMB.toFixed(2), 'MB');
        
        if (pdfSizeMB > 20) { // Warn if over 20MB
          console.warn('PDF is large:', pdfSizeMB.toFixed(2), 'MB. Consider optimizing.');
          
          // Ask user if they want to send email without PDF attachment
          const sendWithoutPDF = window.confirm(
            `PDF file is large (${pdfSizeMB.toFixed(2)}MB) and may cause email delivery issues. Would you like to send the email without PDF attachment? You can download the PDF separately.`
          );
          
          if (sendWithoutPDF) {
            setSuccessMessage('📧 Sending email without PDF attachment...');
            
            // Send email without PDF attachment
            const emailPromise = axios.post(
              `/api/payment-receipts/send-email-without-pdf`,
              {
                emailTo: selectedCustomer.email,
                subject: `Payment Receipt ${form.receiptNumber} - ${selectedCompanyDetails?.name || selectedCompany?.name || selectedCompanyState || selectedCompanyFromState || 'WYENFOS'}`,
                body: `Dear ${selectedCustomer.name || 'Customer'},\n\nYour payment receipt ${form.receiptNumber} has been generated.\n\nDue to file size limitations, the PDF attachment could not be included in this email.\n\nPlease download your receipt from the application or contact us for assistance.\n\nThank you for your business!`,
                receiptData: tempReceipt
              },
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                  'Content-Type': 'application/json'
                },
                timeout: 30000
              }
            );
            
            const emailTimeoutPromise = new Promise((_, reject) => 
              setTimeout(() => reject(new Error('Email request timed out')), 30000)
            );
            
            await Promise.race([emailPromise, emailTimeoutPromise]);
            
            setSuccessMessage(`✅ Email sent successfully to ${selectedCustomer.email}! (PDF not attached due to size)`);
            
            setTimeout(() => {
              setSuccessMessage(null);
            }, 5000);
            
            setLoading(false);
            return;
          }
        }
        
        // Send email with PDF attachment for new receipts
        setSuccessMessage('📧 Sending email...');
        
        const emailPromise = axios.post(
          `/api/payment-receipts/send-email-with-pdf`,
          {
            emailTo: selectedCustomer.email,
            subject: `Payment Receipt ${form.receiptNumber} - ${selectedCompanyDetails?.name || selectedCompany?.name || selectedCompanyState || selectedCompanyFromState || 'WYENFOS'}`,
            body: `Dear ${selectedCustomer.name || 'Customer'},\n\nPlease find attached your payment receipt from ${selectedCompanyDetails?.name || selectedCompany?.name || selectedCompanyState || selectedCompanyFromState || 'WYENFOS'}.\n\nThank you for your business!`,
            pdfBase64: pdfBase64,
            receiptData: tempReceipt
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            timeout: 30000 // 30 second timeout
          }
        );
        
        const emailTimeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Email request timed out')), 30000)
        );
        
        await Promise.race([emailPromise, emailTimeoutPromise]);
        
        setSuccessMessage(`✅ Email sent successfully to ${selectedCustomer.email}!`);
        
        // Clear success message after 5 seconds
        setTimeout(() => {
          setSuccessMessage(null);
        }, 5000);
        
        setLoading(false);
        return;
      }
      
      // For existing receipts, send email without PDF (since we don't have pdfBase64)
      setSuccessMessage('📧 Sending email...');
      
      const emailPromise = axios.post(
        `/api/payment-receipts/send-email-without-pdf`,
        {
          emailTo: selectedCustomer.email,
          subject: `Payment Receipt ${tempReceipt.receiptNo} - ${selectedCompanyDetails?.name || selectedCompany?.name || selectedCompanyState || selectedCompanyFromState || 'WYENFOS'}`,
          body: `Dear ${selectedCustomer.name || 'Customer'},\n\nPlease find attached your payment receipt from ${selectedCompanyDetails?.name || selectedCompany?.name || selectedCompanyState || selectedCompanyFromState || 'WYENFOS'}.\n\nThank you for your business!`,
          receiptData: tempReceipt
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          timeout: 30000 // 30 second timeout
        }
      );
      
      const emailTimeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Email request timed out')), 30000)
      );
      
      await Promise.race([emailPromise, emailTimeoutPromise]);
      
      setSuccessMessage(`✅ Email sent successfully to ${selectedCustomer.email}!`);
      
      // Clear success message after 5 seconds
      setTimeout(() => {
        setSuccessMessage(null);
      }, 5000);
      
      setLoading(false);
      return;
    } catch (error) {
      console.error('Email error:', error.response?.data || error.message);
      
      if (error.response?.status === 404) {
        setError('Email service not available. Please ensure the server is running and the email service is configured properly.');
      } else if (error.response?.status === 500) {
        setError('Server error occurred while sending email. Please try again or contact support.');
      } else if (error.message.includes('timed out')) {
        setError(`Email process timed out. Please try again. If the problem persists, try downloading the PDF and sending it manually.`);
      } else if (error.response?.data?.message?.includes('PDF file too large')) {
        setError(`PDF file is too large (${error.response?.data?.fileSize || 'unknown size'}). Please try downloading the PDF and sending it manually, or contact support for assistance.`);
      } else if (error.response?.data?.message?.includes('Authentication')) {
        setError('Email authentication failed. Please contact support to check email configuration.');
      } else {
        setError(error.response?.data?.message || 'Failed to send email. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleReceiptClick = (receiptId) => {
    setHighlightedReceiptId(receiptId);
    const receipt = receipts.find(r => r._id === receiptId);
    if (receipt) {
      setSelectedCustomer({
        _id: receipt.customerId,
        name: receipt.customerName,
        customerId: receipt.customerId,
        address: receipt.metadata?.address || '',
        phone: receipt.metadata?.phone || '',
        email: receipt.metadata?.email || '',
        gstin: receipt.metadata?.gstin || ''
      });
      setForm({
        receiptNumber: receipt.receiptNo,
        date: new Date(receipt.date).toISOString().split('T')[0],
        remarks: receipt.metadata?.remarks || '',
        customer: {
          name: receipt.customerName || '',
          address: receipt.metadata?.address || '',
          phone: receipt.metadata?.phone || '',
          email: receipt.metadata?.email || '',
          gstin: receipt.metadata?.gstin || ''
        }
      });
      if (receipt.customerId) {
        fetchCustomerBills(receipt.customerId);
      }
    }
  };

  const handleEdit = async () => {
    if (!highlightedReceiptId) {
      setError('Please select a receipt to edit.');
      return;
    }
    
    if (!selectedCustomer) {
      setError('Please select a customer.');
      return;
    }
    
    if (!selectedCustomer._id) {
      setError('Customer ID is required.');
      return;
    }
    
    // More flexible ID validation - allow both MongoDB ObjectId (24 chars) and Firestore IDs (20 chars)
    const idRegex = /^[0-9a-zA-Z]{20,24}$/;
    if (!idRegex.test(selectedCustomer._id)) {
      console.log('Customer ID validation failed:', selectedCustomer._id);
      console.log('Customer ID length:', selectedCustomer._id.length);
      setError('Invalid customer ID format.');
      return;
    }

    // Calculate amounts based on whether a debit note is selected
    const finalTotalProductAmount = selectedDebitNoteId && selectedDebitNoteDetails 
      ? (selectedDebitNoteDetails.totalAmount || selectedDebitNoteDetails.grandTotal || 0)
      : totalProductAmount;
    
    const finalTotalPaidAmount = selectedDebitNoteId && selectedDebitNoteDetails
      ? (selectedDebitNoteDetails.amountPaid || 0)
      : totalPaidAmount;

    if (!form.totalPaidAmount || form.totalPaidAmount <= 0) {
      setError('Total paid amount must be greater than zero.');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const updateData = {
        receiptNo: form.receiptNumber,
        receiptNumber: form.receiptNumber,
        customerId: selectedCustomer.customerId || selectedCustomer._id,
        customerName: form.customer.name || selectedCustomer.name || 'N/A',
        amount: finalTotalPaidAmount,
        date: new Date(form.date),
        type: 'payment',
        creditBillId: creditBills.length > 0 ? creditBills[0]._id : undefined,
        debitNoteId: selectedDebitNoteId || undefined,
        metadata: {
          creditBills: creditBills.map(bill => ({
            _id: bill._id,
            creditBillNo: bill.creditBillNo,
            date: new Date(bill.date),
            totalAmount: bill.totalAmount
          })),
          debitNotes: debitNotes.map(note => ({
            _id: note._id,
            debitNoteNo: note.debitNoteNo,
            date: new Date(note.date),
            amountPaid: note.amountPaid
          })),
          selectedDebitNote: selectedDebitNoteId ? availableDebitNotes.find(note => note._id === selectedDebitNoteId) : null,
          totalProductAmount: finalTotalProductAmount,
          totalPaidAmount: finalTotalPaidAmount,
          remainingBalance: finalTotalProductAmount - finalTotalPaidAmount,
          remarks: form.remarks,
          address: form.customer.address,
          phone: form.customer.phone,
          email: form.customer.email,
          gstin: form.customer.gstin,
          receiptDelivery: {
            email: selectedCustomer.email || form.customer.email,
            mobile: selectedCustomer.phone || form.customer.phone,
            deliveryMethod: 'email_and_sms'
          }
        },
        lastUpdatedBy: userId || 'unknown'
      };
      await axios.put(
        `/api/payment-receipts/${highlightedReceiptId}`,
        updateData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      setSuccessMessage('✅ Receipt updated successfully!');
      fetchReceipts();
      
      // Clear success message after 5 seconds
      setTimeout(() => {
        setSuccessMessage(null);
      }, 5000);
    } catch (error) {
      console.error('Edit error:', error);
      setError(error.response?.data?.message || 'Failed to update receipt');
    }
  };

  const handleDelete = async () => {
    if (!highlightedReceiptId) {
      setError('Please select a receipt to delete.');
      return;
    }

    const receipt = receipts.find(r => r._id === highlightedReceiptId);
    const receiptInfo = receipt ? `${receipt.receiptNumber} - ${receipt.customerName}` : 'this receipt';
    
    if (!window.confirm(`Are you sure you want to delete ${receiptInfo}? This action cannot be undone.`)) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/payment-receipts/${highlightedReceiptId}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setSuccessMessage(`✅ Receipt ${receiptInfo} deleted successfully!`);
      fetchReceipts();
      setHighlightedReceiptId(null);
      
      // Clear success message after 5 seconds
      setTimeout(() => {
        setSuccessMessage(null);
      }, 5000);
    } catch (error) {
      console.error('Delete error:', error);
      setError(error.response?.data?.message || 'Failed to delete receipt');
    }
  };

  return (
    <div className="receipt-page">
      {/* Watermark */}
      <div className="receipt-watermark">
        <img src={watermark} alt="Watermark" />
      </div>
      {/* Cancelled Watermark */}
      {form.isCancelled && (
        <div className="receipt-cancelled-watermark">
          <img src={cancelledWatermark} alt="Cancelled" />
        </div>
      )}
      <div className="receipt-container">
        {error && (
          <div className="error-banner">
            {error}
            <button onClick={() => setError(null)}>Dismiss</button>
          </div>
        )}

        {successMessage && (
          <div className="success-banner">
            <div style={{ whiteSpace: 'pre-line' }}>{successMessage}</div>
            <button onClick={() => setSuccessMessage(null)}>Dismiss</button>
          </div>
        )}

        {loading && <div className="loading-overlay">Loading...</div>}

        {showRequestModal && (
          <div className="modal-overlay">
            <div className="change-request-modal">
              <h3>Request Changes</h3>
              <textarea
                value={requestChanges}
                onChange={(e) => setRequestChanges(e.target.value)}
                placeholder="Describe the changes you'd like to request..."
              />
              <div className="modal-actions">
                <button onClick={() => setShowRequestModal(false)}>Cancel</button>
                <button onClick={handleRequestChange}>Submit Request</button>
              </div>
            </div>
          </div>
        )}



        <div className="bill-header">
          <div className="header-left-section">
            <div className="company-logo-container-header">
              <img
                src={getLogoUrl(selectedCompanyDetails?.logo || selectedCompany?.logo, selectedCompanyDetails?.name || selectedCompany?.name || selectedCompanyState || selectedCompanyFromState || 'WYENFOS')}
                alt="Company Logo"
                className="company-logo-header"
                onError={(e) => {
                  e.target.src = defaultLogo;
                }}
              />
            </div>
            <div className="company-address-header">
              <h4 className="company-name-header">{selectedCompanyDetails?.name || selectedCompany?.name || selectedCompanyState || selectedCompanyFromState || 'WYENFOS'}</h4>
              <p className="address-text-header">{selectedCompanyDetails?.address || selectedCompany?.address || 'Thekkekara Arcade, Chelakottukara, Thrissur, Kerala, 680001'}</p>
              <div className="company-details-row">
                {(selectedCompanyDetails?.GSTIN || selectedCompany?.GSTIN) && <p className="gstin-text-header">GSTIN: {selectedCompanyDetails?.GSTIN || selectedCompany?.GSTIN}</p>}
                {(selectedCompanyDetails?.email || selectedCompany?.email) && <p className="contact-text-header">{selectedCompanyDetails?.email || selectedCompany?.email}</p>}
              </div>
              <div className="company-details-row">
                {(selectedCompanyDetails?.mobile || selectedCompany?.mobile) && <p className="contact-text-header mobile-number-left">{selectedCompanyDetails?.mobile || selectedCompany?.mobile}</p>}
                {(selectedCompanyDetails?.website || selectedCompany?.website) && <p className="contact-text-header">{selectedCompanyDetails?.website || selectedCompany?.website}</p>}
              </div>
            </div>
          </div>
          
          {/* Bill Information - Right side */}
          <div className="bill-info-header">
            <h2>PAYMENT RECEIPT{form.isCancelled ? ' (CANCELLED)' : ''}</h2>
            <p className="bill-number">Receipt No: {form.receiptNumber || 'PR-1'}</p>
            <p className="date">Date: {form.date ? new Date(form.date).toLocaleDateString('en-IN') : new Date().toLocaleDateString('en-IN')}</p>
            {selectedCustomer?.customerId && <p className="reference">Customer: {selectedCustomer.customerId}</p>}
          </div>
        </div>

        <div className="customer-details">
          <div className="detail-item full-width customer-selector">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <label>Select Customer</label>
            </div>
            <select 
              value={selectedCustomer?._id || selectedCustomer?.id || ''} 
              onChange={handleCustomerSelect}
              onFocus={() => {
                console.log('Dropdown focused - Current customers:', customers.length);
                console.log('Current selected value:', selectedCustomer?._id || selectedCustomer?.id || '');
                console.log('Available options:', customers.map(c => ({ id: c._id || c.id, name: c.name || c.customerName })));
              }}
            >
              <option value="">Select a customer</option>
              {customers.map((customer, index) => {
                const displayId = customer.customerId || `CUST${(customer._id || customer.id)?.slice(-4) || '0000'}`;
                const customerId = customer._id || customer.id;
                return (
                  <option key={customerId || `temp-${displayId}-${index}`} value={customerId}>
                    {customer.name || customer.customerName || 'Unknown'} (ID: {displayId})
                  </option>
                );
              })}
            </select>
          </div>
          {selectedCustomer && (
      <>
      <div className="detail-item full-width email-receipt-banner">
        <div style={{ 
          backgroundColor: '#e8f5e8', 
          border: '1px solid #4caf50', 
          borderRadius: '4px', 
          padding: '8px 12px', 
          marginBottom: '15px',
          fontSize: '14px',
          color: '#2e7d32'
        }}>
          📧 <strong>Email Receipt:</strong> Add customer email and mobile for receipt delivery
        </div>
      </div>
      
      <div className="detail-item">
        <label>Customer Name</label>
        <input
          type="text"
          value={selectedCustomer.name || ''}
          readOnly
          style={{ backgroundColor: '#f5f5f5' }}
        />
      </div>
      
      <div className="detail-item">
        <label>Customer ID</label>
        <input
          type="text"
          value={selectedCustomer.customerId || selectedCustomer._id?.slice(-4) || 'N/A'}
          readOnly
          style={{ backgroundColor: '#f5f5f5' }}
        />
      </div>
      
      <div className="detail-item">
        <label>Email Address *</label>
        <input
          type="email"
          name="email"
          value={selectedCustomer.email || ''}
          onChange={handleCustomerDetailChange}
          placeholder="Enter email for receipt delivery"
          required
        />
      </div>
      
      <div className="detail-item">
        <label>Mobile Number *</label>
        <input
          type="tel"
          name="phone"
          value={selectedCustomer.phone || ''}
          onChange={handleCustomerDetailChange}
          placeholder="Enter mobile number for SMS receipt"
          required
        />
      </div>
      
                {availableDebitNotes.length > 0 && (
            <div className="detail-item full-width debit-note-selector">
              <label>Select Debit Note (Optional)</label>
              <select 
                value={selectedDebitNoteId} 
                onChange={(e) => handleDebitNoteSelect(e.target.value)}
              >
                <option value="">Select a debit note</option>
                {availableDebitNotes.map((note, index) => (
                  <option key={note._id || note.id || `debit-${note.debitNoteNo}-${index}`} value={note._id || note.id || ''}>
                    {note.debitNoteNo} - ₹{note.amountPaid?.toFixed(2)} - {new Date(note.date).toLocaleDateString('en-IN')}
                  </option>
                ))}
              </select>
              <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                {availableDebitNotes.length > 0 ? `${availableDebitNotes.length} debit notes available` : 'No debit notes available'}
              </div>
            </div>
          )}
          
    </>
          )}
        </div>

    
        {(creditBills.length > 0 || debitNotes.length > 0) && (
          <>
       

            {/* Receipt Summary - Show different content based on debit note selection */}
            <div className="receipt-summary">
              {selectedDebitNoteId && selectedDebitNoteDetails ? (
                <>
                  {/* Summary for Selected Debit Note */}
                  <div className="debit-note-summary">
                    <div className="amount-input-group">
                      <label>Total Amount of Product:</label>
                      <input
                        type="number"
                        value={selectedDebitNoteDetails.totalAmount || selectedDebitNoteDetails.grandTotal || 0}
                        onChange={(e) => {
                          const newValue = parseFloat(e.target.value) || 0;
                          setSelectedDebitNoteDetails(prev => ({
                            ...prev,
                            totalAmount: newValue,
                            grandTotal: newValue
                          }));
                        }}
                        placeholder="Enter total product amount"
                        step="0.01"
                        min="0"
                        style={{
                          padding: '8px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          fontSize: '14px',
                          width: '150px'
                        }}
                      />
                      <span style={{ marginLeft: '10px', fontWeight: 'bold' }}>₹</span>
                    </div>
                    
                    <div className="amount-input-group">
                      <label>Total Amount Paid:</label>
                      <input
                        type="number"
                        value={selectedDebitNoteDetails.amountPaid || 0}
                        onChange={(e) => {
                          const newValue = parseFloat(e.target.value) || 0;
                          setSelectedDebitNoteDetails(prev => ({
                            ...prev,
                            amountPaid: newValue
                          }));
                        }}
                        placeholder="Enter amount paid"
                        step="0.01"
                        min="0"
                        style={{
                          padding: '8px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          fontSize: '14px',
                          width: '150px'
                        }}
                      />
                      <span style={{ marginLeft: '10px', fontWeight: 'bold' }}>₹</span>
                    </div>
                    
                    {/* Payment Status Warning */}
                    {Math.abs((selectedDebitNoteDetails.amountPaid || 0) - (selectedDebitNoteDetails.totalAmount || selectedDebitNoteDetails.grandTotal || 0)) > 0.01 && (
                      <div style={{ 
                        backgroundColor: '#ffebee', 
                        border: '1px solid #f44336', 
                        borderRadius: '4px', 
                        padding: '8px', 
                        marginTop: '10px',
                        color: '#c62828',
                        fontSize: '14px'
                      }}>
                        ⚠️ <strong>Partial Payment:</strong> Payment receipt can only be generated for full payment. 
                        Amount paid must equal total product amount.
                      </div>
                    )}
                    
                    {/* Full Payment Confirmation */}
                    {Math.abs((selectedDebitNoteDetails.amountPaid || 0) - (selectedDebitNoteDetails.totalAmount || selectedDebitNoteDetails.grandTotal || 0)) <= 0.01 && (
                      <div className="full-payment-message" style={{ 
                        backgroundColor: '#e8f5e8', 
                        border: '1px solid #4caf50', 
                        borderRadius: '4px', 
                        padding: '8px', 
                        marginTop: '10px',
                        color: '#2e7d32',
                        fontSize: '14px'
                      }}>
                        ✅ <strong>Full Payment:</strong> Payment receipt can be generated.
                      </div>
                    )}
                  </div>
                  
                  {/* Selected Debit Note Details */}
                  <div className="selected-debit-note-summary">
                    <h4>Selected Debit Note Details</h4>
                    <div className="debit-note-info">
                      <p><strong>Debit Note No:</strong> <span>{selectedDebitNoteDetails.debitNoteNo || selectedDebitNoteDetails.invoiceNumber || 'N/A'}</span></p>
                      <p><strong>Date:</strong> <span>{selectedDebitNoteDetails.date ? new Date(selectedDebitNoteDetails.date).toLocaleDateString('en-IN') : 'N/A'}</span></p>
                      <p><strong>Amount Paid:</strong> <span>₹{selectedDebitNoteDetails.amountPaid?.toFixed(2) || '0.00'}</span></p>
                      <p><strong>Payment Status:</strong> <span className="payment-status-badge">✅ Paid</span></p>
                      {selectedDebitNoteDetails.remarks && (
                        <p><strong>Remarks:</strong> <span>{selectedDebitNoteDetails.remarks}</span></p>
                      )}
                    </div>
                  </div>
                  

                </>
              ) : (
                <>
                  <p>Total Amount of Product: <span>₹{totalProductAmount.toFixed(2)}</span></p>
                  <p>Total Amount Paid: <span>₹{totalPaidAmount.toFixed(2)}</span></p>
                </>
              )}
              
              {/* Payment Status Summary */}
              <div className="payment-status-summary">
                <p>Fully Paid Bills: <span className="fully-paid-count">
                  {creditBills.filter(bill => {
                    const totalAmount = bill.totalAmount || 0;
                    const totalPaid = bill.totalPaid || 0;
                    return (totalAmount - totalPaid) <= 0;
                  }).length}
                </span></p>
                <p>Partially Paid Bills: <span className="partially-paid-count">
                  {creditBills.filter(bill => {
                    const totalAmount = bill.totalAmount || 0;
                    const totalPaid = bill.totalPaid || 0;
                    return (totalAmount - totalPaid) > 0;
                  }).length}
                </span></p>
                <p>Total Bills: <span>{creditBills.length}</span></p>
              </div>
              
              {remainingBalance === 0 && (
                <div className="verification-note">
                  <p>✓ <strong>FULLY PAID:</strong> Customer has been paid the complete amount as per the date {form.date}</p>
                  <p>✓ All outstanding bills have been settled</p>
                </div>
              )}
              
              {remainingBalance > 0 && (
                <div className="outstanding-note">
                  <p>⚠ <strong>OUTSTANDING:</strong> Customer still has outstanding balance of ₹{remainingBalance.toFixed(2)}</p>
                  <p>Please ensure all payments are completed before marking as fully paid</p>
                </div>
              )}
            </div>
          </>
        )}

        {/* Receipt Options */}
        <div className="receipt-options">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={form.isCancelled}
              onChange={(e) => setForm(prev => ({ ...prev, isCancelled: e.target.checked }))}
              className="cancel-checkbox"
            />
            Cancel Receipt
          </label>
        </div>

        {/* Product Details Section */}
        <div className="product-details-section">
          <h3>Product Details</h3>
          <div className="product-table">
            <table>
              <thead>
                <tr>
                  <th>Product Name</th>
                  <th>Quantity</th>
                  <th>Rate (₹)</th>
                  <th>Amount (₹)</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {form.products.map((product, index) => (
                  <tr key={product.id}>
                    <td>
                      <input
                        type="text"
                        value={product.name}
                        onChange={(e) => updateProduct(product.id, 'name', e.target.value)}
                        placeholder="Enter product name"
                        className="product-input"
                        disabled={form.isCancelled}
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        value={product.quantity}
                        onChange={(e) => updateProduct(product.id, 'quantity', parseFloat(e.target.value) || 0)}
                        min="1"
                        className="product-input quantity-input"
                        disabled={form.isCancelled}
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        value={product.rate}
                        onChange={(e) => updateProduct(product.id, 'rate', parseFloat(e.target.value) || 0)}
                        min="0"
                        step="0.01"
                        className="product-input rate-input"
                        disabled={form.isCancelled}
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        value={product.amount.toFixed(2)}
                        readOnly
                        className="product-input amount-input"
                      />
                    </td>
                    <td>
                      {form.products.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeProduct(product.id)}
                          className="remove-product-btn"
                          disabled={form.isCancelled}
                        >
                          ✕
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="product-actions">
              <button type="button" onClick={addProduct} className="add-product-btn" disabled={form.isCancelled}>
                + Add Product
              </button>
            </div>
          </div>

          {/* Amount Summary */}
          <div className="amount-summary">
            <div className="amount-row">
              <label>Total Product Amount:</label>
              <span>₹{form.totalProductAmount.toFixed(2)}</span>
            </div>
            <div className="amount-row total-paid">
              <label>Total Paid Amount:</label>
              <input
                type="number"
                value={form.totalPaidAmount}
                onChange={(e) => setForm(prev => ({
                  ...prev,
                  totalPaidAmount: parseFloat(e.target.value) || 0
                }))}
                min="0"
                step="0.01"
                className="paid-amount-input"
                disabled={form.isCancelled}
              />
            </div>
            {form.totalProductAmount > 0 && form.totalPaidAmount > 0 && (
              <div className="amount-row balance">
                <label>Balance:</label>
                <span className={form.totalProductAmount - form.totalPaidAmount === 0 ? 'balance-zero' : 'balance-pending'}>
                  ₹{(form.totalProductAmount - form.totalPaidAmount).toFixed(2)}
                </span>
              </div>
            )}
          </div>
        </div>

        <div className="remarks-signature">
          <div className="remarks">
            <label>Remarks</label>
            <textarea
              value={form.remarks}
              onChange={(e) => setForm({ ...form, remarks: e.target.value })}
              placeholder="Enter remarks"
              disabled={form.isCancelled}
            />
          </div>
          {/* Signature removed - handled in PDF generation */}
        </div>

        <div className="thank-you-section">
          <h3>Thank You for Your Business!</h3>
          <p>We appreciate your trust and look forward to serving you again.</p>
        </div>

        <div className="receipt-actions">
          <button 
            onClick={saveReceipt} 
            className="receipt-action-btn receipt-save-btn"
            disabled={
              form.isCancelled ||
              (selectedDebitNoteId && selectedDebitNoteDetails && 
              Math.abs((selectedDebitNoteDetails.amountPaid || 0) - (selectedDebitNoteDetails.totalAmount || selectedDebitNoteDetails.grandTotal || 0)) > 0.01)
            }
            style={{
              opacity: (
                selectedDebitNoteId && selectedDebitNoteDetails && 
                Math.abs((selectedDebitNoteDetails.amountPaid || 0) - (selectedDebitNoteDetails.totalAmount || selectedDebitNoteDetails.grandTotal || 0)) > 0.01
              ) ? 0.5 : 1,
              cursor: (
                selectedDebitNoteId && selectedDebitNoteDetails && 
                Math.abs((selectedDebitNoteDetails.amountPaid || 0) - (selectedDebitNoteDetails.totalAmount || selectedDebitNoteDetails.grandTotal || 0)) > 0.01
              ) ? 'not-allowed' : 'pointer'
            }}
          >
            {form.isCancelled
              ? 'Save (Cancelled)'
              : selectedDebitNoteId && selectedDebitNoteDetails && 
                Math.abs((selectedDebitNoteDetails.amountPaid || 0) - (selectedDebitNoteDetails.totalAmount || selectedDebitNoteDetails.grandTotal || 0)) > 0.01
                ? 'Save (Full Payment Required)'
                : 'Save'
            }
          </button>
          <button onClick={printReceipt} className="receipt-action-btn receipt-print-btn">Print</button>
          <button onClick={() => downloadPDF()} className="receipt-action-btn receipt-download-btn">Download</button>
          <button 
            onClick={sendEmail} 
            className="receipt-action-btn receipt-email-btn"
            disabled={(!selectedCustomer?.email && !form.customer.email) || loading}
            title={
              (!selectedCustomer?.email && !form.customer.email) ? 'No customer email available' :
              loading ? 'Sending email...' :
              'Send receipt via email'
            }
          >
            {loading ? '📧 Sending...' : '📧 Send Email'}
          </button>
          <button onClick={() => setShowSideView(!showSideView)} className="receipt-action-btn receipt-toggle-btn">
            {showSideView ? 'Hide Receipts' : 'View Receipts'}
          </button>
          <button onClick={() => navigate(-1)} className="receipt-action-btn receipt-back-btn">Back</button>
          {userRole === 'admin' || userRole === 'super_admin' ? (
            <>
              <button 
                onClick={() => handleEdit()} 
                className="receipt-action-btn receipt-edit-btn"
                disabled={!highlightedReceiptId}
                title={!highlightedReceiptId ? 'Select a receipt to edit' : 'Edit selected receipt'}
              >
                Edit
              </button>
              <button 
                onClick={() => handleDelete()} 
                className="receipt-action-btn receipt-delete-btn"
                disabled={!highlightedReceiptId}
                title={!highlightedReceiptId ? 'Select a receipt to delete' : 'Delete selected receipt'}
              >
                Delete
              </button>
            </>
          ) : null}
          
          {userRole === 'super_admin' ? (
            <button 
              onClick={() => setShowRequestModal(true)} 
              className="receipt-action-btn receipt-request-btn"
              disabled={!highlightedReceiptId}
              title={!highlightedReceiptId ? 'Select a receipt to request changes' : 'Request changes for selected receipt (Super Admin Only)'}
            >
              Request Changes
            </button>
          ) : null}
        </div>
      </div>

      {showSideView && (
        <div className="side-view">
          <h3 className="side-view-title">
            Saved Payment Receipts 
            <span style={{ fontSize: '14px', color: '#666', marginLeft: '10px' }}>
              ({displayedReceipts.length} of {receipts.length})
            </span>
          </h3>
          <div className="side-view-content">
            {displayedReceipts.length > 0 ? (
              displayedReceipts.map((receipt, index) => (
                <div
                  key={receipt._id || receipt.id || `receipt-${index}`}
                  className={`side-view-item ${highlightedReceiptId === receipt._id ? 'highlighted' : ''}`}
                  onClick={() => handleReceiptClick(receipt._id || receipt.id)}
                >
                  <p>
                    {receipt.customerName || 'N/A'} - {new Date(receipt.date).toLocaleDateString()} - ₹{receipt.amount?.toFixed(2) || '0.00'}
                  </p>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      downloadPDF({
                        receiptNo: receipt.receiptNo,
                        date: receipt.date,
                        customer: {
                          name: receipt.customerName,
                          _id: receipt.customerId,
                          address: receipt.metadata?.address,
                          phone: receipt.metadata?.phone,
                          email: receipt.metadata?.email,
                          gstin: receipt.metadata?.gstin
                        },
                        creditBills: receipt.metadata?.creditBills || [],
                        debitNotes: receipt.metadata?.debitNotes || [],
                        totalProductAmount: receipt.metadata?.totalProductAmount || 0,
                        totalPaidAmount: receipt.metadata?.totalPaidAmount || 0,
                        remainingBalance: receipt.metadata?.remainingBalance || 0,
                        remarks: receipt.metadata?.remarks || ''
                      });
                    }}
                    className="download-link"
                  >
                    Download PDF
                  </button>
                </div>
              ))
            ) : (
              <p>No payment receipts available.</p>
            )}
            
            {/* Show More/Less Button */}
            {receipts.length > 10 && (
              <div style={{ 
                textAlign: 'center', 
                marginTop: '15px', 
                padding: '10px',
                borderTop: '1px solid #ddd'
              }}>
                <button
                  onClick={() => setShowAllReceipts(!showAllReceipts)}
                  style={{
                    backgroundColor: '#997a8d',
                    color: 'white',
                    border: 'none',
                    padding: '8px 16px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  {showAllReceipts ? 'Show Latest 10' : `Show All ${receipts.length} Receipts`}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default PaymentReceipt;